import { useState } from 'react';
import { Typography } from '@mui/material';
import { Tooltip } from '@mui/material';
import { Stack } from '@mui/joy';
import InfoRoundedIcon from '@mui/icons-material/InfoRounded';
import { Card, LoadingSpinner, Select } from 'src/components';
import { useDashStore, usePatientSummary } from 'src/hooks';

export const PatientSummary = () => {
  const { selectedUser } = useDashStore();
  const userId = selectedUser?.id || null;
  const isAllUsers = userId === 'All Users';
  const NoUserText = 'Select a user to view their summary';
  const defaultText = 'Summary unavailable';
  const summaryTypeOptions = ['biometrics', 'wellness logs', 'combination'];
  const timeframeOptions = ['week', 'month', 'quarter', 'year', 'all time'];
  const [selectedSummaryType, setSelectedSummaryType] = useState('combination');
  const [selectedTimeframe, setSelectedTimeframe] = useState('month');
  const { summary, loading } = usePatientSummary({
    userId,
    username: selectedUser?.username,
    summaryType: selectedSummaryType,
    timeframe: selectedTimeframe,
    enabled: !!userId && !isAllUsers,
    fallbackText: defaultText,
  });

  const displayText = !userId || isAllUsers ? NoUserText : summary;

  return (
    <Card
      title={
        <Stack direction="row" sx={{ alignItems: 'baseline', gap: '6px' }}>
          <Typography variant="h3">
            {isAllUsers ? 'Patient Summary' : `${selectedUser.username} Summary`}
          </Typography>
          <Tooltip title="AI generated content may be inaccurate or out of date." placement="right">
            <span
              style={{
                display: 'inline-flex',
                cursor: 'help',
                transform: 'translateY(-6px)',
                alignItems: 'baseline',
                gap: '3px',
              }}>
              <span
                style={{
                  background:
                    'linear-gradient(90deg, #ff2d55 0%, #ff9500 16%, #ffcc00 32%, #34c759 48%, #00c7be 64%, #007aff 80%, #af52de 100%)',
                  WebkitBackgroundClip: 'text',
                  backgroundClip: 'text',
                  color: 'transparent',
                  fontWeight: 700,
                }}>
                ✨Beta
              </span>
              <InfoRoundedIcon
                sx={{
                  fontSize: '0.8rem',
                  color: '#af52de',
                }}
              />
            </span>
          </Tooltip>
        </Stack>
      }
      headerContent={
        <div style={{ display: 'flex', gap: '8px' }}>
          <Select
            options={summaryTypeOptions}
            value={selectedSummaryType}
            setValue={setSelectedSummaryType}
            sx={{ minWidth: '155px', maxHeight: '40px' }}
          />
          <Select
            options={timeframeOptions}
            value={selectedTimeframe}
            setValue={setSelectedTimeframe}
            sx={{ minWidth: '120px', maxHeight: '40px' }}
          />
        </div>
      }
      sx={{ marginTop: '20px', minHeight: '175px', maxHeight: '175px', boxSizing: 'border-box' }}>
      <div
        style={{
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: loading ? 'center' : 'flex-start',
          justifyContent: loading ? 'center' : 'flex-start',
          overflow: 'auto',
          paddingTop: '8px',
          marginRight: '40px',
        }}>
        {loading ? (
          <LoadingSpinner size={28} />
        ) : (
          <Typography variant="body2" style={{ textAlign: 'left' }}>
            {displayText}
          </Typography>
        )}
      </div>
    </Card>
  );
};

export default PatientSummary;
